/**
 * @fileoverview Pure dedup helpers for bank transaction entries
 * @module jobs/summarization/agents/bankTransactions/dedupCore
 *
 * Contains no Convex server imports so it can run both server-side and in the
 * browser. The frontend computes dedup over the full accumulated transaction
 * list (loaded page by page) because fingerprint groups span source documents
 * and a single Convex query execution cannot read every entry on large jobs
 * without exceeding the 16 MiB read limit.
 *
 * Fingerprint = normalizedAccountNumber | normalizedDate | amount(2dp) | direction | normalizedDescription
 *
 * Within a fingerprint group, the earliest-created row across all source
 * documents is the primary. Subsequent rows are duplicates. The legacy
 * "legitimate repeats per document" carve-out has been removed because in
 * practice it masked extraction/re-upload duplicates more often than it
 * preserved genuine same-day repeats. Practitioners can use `dedupOverride`
 * to recover any false positive.
 */

import type { Id } from "@cvx/_generated/dataModel";

function normalizeAccountNumber(value: string | undefined): string {
  if (!value) return "";
  const stripped = value.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
  if (!stripped) return "";
  if (/^\d+$/.test(stripped) && stripped.length >= 12) {
    return stripped.slice(6);
  }
  return stripped;
}

export function normalizeDate(value: string | undefined): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const [d, m, y] = trimmed.split("/");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const match = trimmed.match(/^(\d{1,2})\s+(\w{3,})\s+(\d{4})$/);
  if (match) {
    const months: Record<string, string> = {
      jan: "01",
      feb: "02",
      mar: "03",
      apr: "04",
      may: "05",
      jun: "06",
      jul: "07",
      aug: "08",
      sep: "09",
      oct: "10",
      nov: "11",
      dec: "12",
    };
    const mKey = match[2].toLowerCase().slice(0, 3);
    const mNum = months[mKey] ?? "00";
    return `${match[3]}-${mNum}-${match[1].padStart(2, "0")}`;
  }
  return trimmed.toLowerCase();
}

function normalizeDescription(value: string | undefined): string {
  if (!value) return "";
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

export function buildFingerprint(entry: {
  accountNumber?: string;
  date: string;
  amount: number;
  direction: string;
  description: string;
}): string {
  return [
    normalizeAccountNumber(entry.accountNumber),
    normalizeDate(entry.date),
    entry.amount.toFixed(2),
    entry.direction,
    normalizeDescription(entry.description),
  ].join("|");
}

export interface DedupEntryInput {
  _id: Id<"bankTransactionEntries">;
  _creationTime: number;
  accountNumber?: string;
  date: string;
  amount: number;
  direction: string;
  description: string;
  dedupOverride?: {
    action: "force_unique" | "force_duplicate";
    duplicateOfId?: Id<"bankTransactionEntries">;
  };
}

export interface DedupResult {
  fingerprint: string;
  isDuplicate: boolean;
  duplicateOfId?: Id<"bankTransactionEntries">;
}

/**
 * Computes dedup state for a set of entries. Pure function, safe to call
 * from queries and from the browser. Practitioner overrides win over the
 * computed result.
 */
export function computeDedup(
  entries: DedupEntryInput[],
): Map<Id<"bankTransactionEntries">, DedupResult> {
  const results = new Map<Id<"bankTransactionEntries">, DedupResult>();

  // Group by fingerprint (ignoring overridden rows — they're handled separately
  // so they neither claim a primary slot nor get marked as duplicates by the
  // group logic).
  const groups = new Map<string, DedupEntryInput[]>();
  for (const entry of entries) {
    const fp = buildFingerprint(entry);
    if (entry.dedupOverride) continue;
    const bucket = groups.get(fp) ?? [];
    bucket.push(entry);
    groups.set(fp, bucket);
  }

  for (const [fp, group] of groups) {
    group.sort((a, b) => a._creationTime - b._creationTime);
    const primaryId = group[0]._id;
    for (let i = 0; i < group.length; i++) {
      results.set(group[i]._id, {
        fingerprint: fp,
        isDuplicate: i > 0,
        duplicateOfId: i > 0 ? primaryId : undefined,
      });
    }
  }

  // Apply overrides on top.
  for (const entry of entries) {
    if (!entry.dedupOverride) continue;
    const fp = buildFingerprint(entry);
    if (entry.dedupOverride.action === "force_unique") {
      results.set(entry._id, { fingerprint: fp, isDuplicate: false });
    } else {
      results.set(entry._id, {
        fingerprint: fp,
        isDuplicate: true,
        duplicateOfId: entry.dedupOverride.duplicateOfId,
      });
    }
  }

  return results;
}
